/*
  Accessibility repairs for third-party and merchant-authored markup that the
  theme does not render itself. Found in the store-wide axe audit, 2026-09-28.

  1. Embedded iframes (YouTube and similar) pasted into product descriptions,
     blog articles and pages often have no title, so screen readers announce
     an unnamed frame (WCAG 4.1.2, axe frame-title).
  2. Junip's review "Filter" toggle points aria-controls at a container that
     only exists once the panel is open (axe aria-valid-attr-value). Drop the
     attribute while the target is missing and restore it when it appears.
  3. Rebuy recommendation and Smart Cart headings, product groups, and the
     cart dialog's accessible name.

  Runs on load and on DOM changes, because these widgets render late. Later
  passes only visit the subtrees that changed, run when the main thread is
  idle, and write an attribute only when its value differs, so a repair never
  invalidates style or wakes other observers for markup that is already right.
*/
(() => {
  const frames = document.getElementsByTagName('iframe');
  const junipToggles = document.getElementsByClassName('junip-filters-toggle');
  const recommendationSelector = '.tsf-rebuy .rebuy-product-info > h5, .tsf-rebuy .rebuy-product-block[aria-label]:not([role]), #rebuy-cart .rebuy-product-block[aria-label]:not([role]), #rebuy-cart a.rebuy-cart__flyout-item-product-title[role="heading"], #rebuy-cart .rebuy-widget h4.primary-title, #rebuy-cart .rebuy-widget .rebuy-product-info > h5';

  const candidateSelector = 'h4.primary-title, h5, .rebuy-product-block[aria-label]:not([role]), a.rebuy-cart__flyout-item-product-title[role="heading"]';
  const contextSelector = '.tsf-rebuy, #rebuy-cart';

  const setAttr = (element, name, value) => {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  };

  const nameFrames = () => {
    for (const f of frames) {
      if (f.hasAttribute('title') || f.hasAttribute('aria-label') || f.hasAttribute('aria-labelledby')) continue;
      if (f.closest('[aria-hidden="true"]')) continue;
      const src = f.getAttribute('src') || '';
      if (/youtube|youtu\.be|vimeo|wistia|loom/i.test(src)) f.title = 'Embedded video';
      else if (src) f.title = 'Embedded content';
    }
  };

  const fixJunip = () => {
    for (const b of junipToggles) {
      const id = b.getAttribute('aria-controls') || b.dataset.tsfControls;
      if (!id) continue;
      if (document.getElementById(id)) {
        if (!b.hasAttribute('aria-controls')) b.setAttribute('aria-controls', id);
      } else if (b.hasAttribute('aria-controls')) {
        b.dataset.tsfControls = id;
        b.removeAttribute('aria-controls');
      }
    }
  };

  // Rebuy renders product names as h5 directly beneath the rail's h2.
  // Correct the announced level while preserving widget nodes and styles.
  const fixRecommendation = (element) => {
    if (element.matches('#rebuy-cart a.rebuy-cart__flyout-item-product-title[role="heading"]')) {
      element.removeAttribute('role');
      element.removeAttribute('aria-level');
    } else if (element.matches('.rebuy-product-block')) {
      setAttr(element, 'role', 'group');
    } else if (element.matches('#rebuy-cart .rebuy-widget h4.primary-title')) {
      setAttr(element, 'role', 'heading');
      setAttr(element, 'aria-level', '3');
    } else if (element.matches('#rebuy-cart .rebuy-widget .rebuy-product-info > h5')) {
      setAttr(element, 'role', 'heading');
      // Pantry mode hides the widget title beneath its own level-two heading.
      const pantryActive = element.closest('#rebuy-cart').classList.contains('tsf-cart-pantry-active')
        && element.closest('[data-rebuy-component="cross-sell-widget"]');
      setAttr(element, 'aria-level', pantryActive ? '3' : '4');
    } else if (element.matches('.tsf-rebuy .rebuy-product-info > h5')) {
      setAttr(element, 'role', 'heading');
      setAttr(element, 'aria-level', '3');
    }
  };

  const fixRecommendations = (root) => {
    if (root.nodeType !== 1 && root !== document) return;
    if (root === document) {
      document.querySelectorAll(recommendationSelector).forEach(fixRecommendation);
      return;
    }
    // Match candidates locally, then confirm the full selector, which depends
    // on the rail or cart around the changed subtree.
    if (root.matches(recommendationSelector)) fixRecommendation(root);
    root.querySelectorAll(candidateSelector).forEach((element) => {
      if (element.matches(recommendationSelector)) fixRecommendation(element);
    });
  };

  // The Smart Cart is a modal dialog with no accessible name. Name it from
  // its own visible title when Rebuy renders one, otherwise "Your cart".
  let cartTitleId = 0;
  const nameCartDialog = () => {
    const cart = document.getElementById('rebuy-cart');
    if (!cart) return;
    const dialogs = cart.getAttribute('role') === 'dialog' ? [cart] : [...cart.querySelectorAll('[role="dialog"]')];
    dialogs.forEach((dialog) => {
      if (dialog.hasAttribute('aria-label') && !dialog.hasAttribute('data-tsf-named')) return;
      const labelledBy = dialog.getAttribute('aria-labelledby');
      if (labelledBy && labelledBy.split(/\s+/).some((id) => document.getElementById(id))) return;
      const title = dialog.querySelector('.rebuy-cart__title, .rebuy-cart__flyout-title, .rebuy-cart__flyout-heading');
      if (title && title.textContent.trim()) {
        if (!title.id) title.id = `tsf-rebuy-cart-title-${++cartTitleId}`;
        setAttr(dialog, 'aria-labelledby', title.id);
        if (dialog.hasAttribute('data-tsf-named')) {
          dialog.removeAttribute('aria-label');
          dialog.removeAttribute('data-tsf-named');
        }
      } else {
        // Any aria-labelledby left here points only at missing ids.
        if (labelledBy) dialog.removeAttribute('aria-labelledby');
        setAttr(dialog, 'aria-label', 'Your cart');
        setAttr(dialog, 'data-tsf-named', '');
      }
    });
  };

  const pending = new Set();
  let fullPass = true;
  let queued = false;
  const run = () => {
    queued = false;
    const roots = fullPass ? [document] : [...pending].filter((root) => root.isConnected);
    fullPass = false;
    pending.clear();
    nameFrames();
    fixJunip();
    roots.forEach(fixRecommendations);
    nameCartDialog();
  };
  const idle = window.requestIdleCallback
    ? (callback) => window.requestIdleCallback(callback, { timeout: 300 })
    : (callback) => window.setTimeout(callback, 16);
  const schedule = () => {
    if (queued) return;
    queued = true;
    idle(run);
  };

  let observedCart = null;
  const watchCart = (observer) => {
    const cart = document.getElementById('rebuy-cart');
    if (!cart || cart === observedCart) return;
    observedCart = cart;
    // Pantry mode changes heading levels inside the cart; opening it toggles
    // aria-hidden. Follow those two attributes only.
    observer.observe(cart, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
  };

  const observer = new MutationObserver((records) => {
    let changed = false;
    let cartChanged = false;
    for (const record of records) {
      if (record.type === 'attributes') {
        if (record.target.id === 'rebuy-cart') { pending.add(record.target); changed = true; cartChanged = true; }
        continue;
      }
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        changed = true;
        if (!cartChanged && (node.id === 'rebuy-cart' || node.closest('#rebuy-cart'))) cartChanged = true;
        // A node added inside an existing rail or cart is only meaningful in
        // that context; visit the node itself and everything below it.
        if (node.closest(contextSelector) || node.querySelector(contextSelector)) pending.add(node);
      }
      if (record.removedNodes.length && junipToggles.length) changed = true;
    }
    if (!changed) return;
    watchCart(observer);
    // Name the cart dialog in the same task that renders or opens it, so it
    // is announced with its name; the other repairs wait for idle time.
    if (cartChanged) nameCartDialog();
    schedule();
  });

  const start = () => {
    run();
    observer.observe(document.documentElement, { childList: true, subtree: true });
    watchCart(observer);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
